import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  IconManager,
  setIconManager as setGlobalIconManager,
} from '@builder4/shared/icons/manager';
import { loadFontAwesomeData } from '@builder4/shared/icons/data';

interface IconContextType {
  iconManager: IconManager | null;
  isLoading: boolean;
}

const IconContext = createContext<IconContextType | null>(null);

export const useIcons = () => {
  const context = useContext(IconContext);
  if (!context) {
    throw new Error('useIcons must be used within an IconProvider');
  }
  return context;
};

interface IconProviderProps {
  children: React.ReactNode;
}

export const IconProvider: React.FC<IconProviderProps> = ({ children }) => {
  const [iconManager, setIconManager] = useState<IconManager | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const initializeIcons = async () => {
      try {
        const data = await loadFontAwesomeData();

        if (cancelled) return;

        const manager = new IconManager(data.icons, data.categories);
        setGlobalIconManager(manager);
        setIconManager(manager);
        setIsLoading(false);
      } catch (error) {
        console.error('Failed to load icons:', error);
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    initializeIcons();

    return () => {
      cancelled = true;
    };
  }, []);

  const value: IconContextType = {
    iconManager,
    isLoading,
  };

  return <IconContext.Provider value={value}>{children}</IconContext.Provider>;
};
